/* eslint-disable no-constant-condition */
import { unwrapResult } from '@reduxjs/toolkit'
import { Col, Dropdown, DropDownProps, Input, Menu, Row, Select } from 'antd'
import React, {
  forwardRef,
  ReactElement,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootDispatch } from '../../../../app/store'
import { CommonCode, CommonCodeCategory } from '../../../../models/commoncode'
import { RecipeCategory } from '../../../../models/recipe'
import { ThemeContext } from '../../../../theme/ThemeContext'
import { sleep } from '../../../../utils/SystemUtils'
import { getCodeCompletions, } from '../../../completion/completionSlice'
import { selectSettings } from '../../../user/setting/settingSlice'
import { Checks } from '../../../../models/setting'
import styles from './RecipeItemSelect.module.css'

const { Option } = Select
export interface RecipeItemFieldInputRef {
  focus: () => void
  select: () => void
  isDropdownOpen?: () => boolean
}

const NumberPattern = /^[1-9]\d*$/

const NumberPatternTwoCount = /^[0-9]+((\.)[0-9]{0,2})?$/

const CountPerTipNumberPattern = /^([1-9]\d*(\.\d*)?)|(0\.\d*[1-9][0-9])|(0\.\d*[1-9])$/

// const NormalDosePattern = /([0-9]\d*(\.\d*[1-9])?)|(0\.\d*[1-9])/;
const NormalDosePattern = /^\d+(\.\d*)?$/

// const DecimalPattern = /([0-9]\d*(\.\d*[1-9])?)|(0\.\d*[1-9])/;
const DecimalPattern = /^\d+(\.\d*)?$/

const ColumnsPerRow = 4

interface ListItemProps {
  active: boolean
  code: CommonCode
  onSelect: (code: CommonCode) => void
}

const ListItem = ({ active, code, onSelect }: ListItemProps): ReactElement => {
  const theme = useContext(ThemeContext)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (active) {
      ref.current?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
      })
    }
  }, [active])
  return (
    <Col
      ref={ref}
      span={24 / ColumnsPerRow}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onSelect(code)
      }}
      style={{
        color: active ? theme.tt : theme.tc1,
        fontSize: '1rem',
        textAlign: 'center',
        lineHeight: '2rem',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
        padding: '0 5px',
        cursor: 'pointer',
        backgroundColor: active ? theme.c1 : undefined,
      }}
    >
      {code.code ? `${code.name}(${code.code})` : code.name}
    </Col>
  )
}

interface RecipeItemFieldInputProps
  extends Pick<DropDownProps, 'getPopupContainer'> {
  selectData?: any
  yzcfkf?: boolean //医嘱处方页面标志
  editTableDoseUnit?: boolean //是否是编辑剂量单位
  field: string
  recipeCategory?: RecipeCategory
  disabled?: boolean
  value?: string | number
  onChange?: (value: string, normalFrequency?: number) => void
  onBlur?: (v?: any) => void
  onPressEnter?: (v?: any) => void
  onKeyDown?: (v?: any) => void
  onChangeAll?: () => void
  noSize?: boolean
  kskdTag?: boolean
  ydkdFlag?: boolean
  isTbEdit?: boolean
  record?: any
  placeholderInput?: string
  style?: React.CSSProperties
  autoFocus?: boolean
  clearDataFlag?: any
  currentDoseUnit?: string
}

const RealRecipeItemFieldInput = (
  {
    selectData,
    yzcfkf = false,
    editTableDoseUnit = false,
    field,
    recipeCategory,
    disabled = false,
    value,
    onChange,
    onBlur,
    onPressEnter,
    onKeyDown,
    style,
    autoFocus = false,
    clearDataFlag,
    noSize = false,
    kskdTag = false,
    ydkdFlag = false,
    isTbEdit = false,
    record,
    getPopupContainer = (node) => node.parentElement || document.body,
    onChangeAll,
    placeholderInput = '',
    currentDoseUnit,
    ...others
  }: RecipeItemFieldInputProps,
  ref: React.Ref<RecipeItemFieldInputRef>
): ReactElement => {
  const theme = useContext(ThemeContext)

  const dispatch = useDispatch<RootDispatch>()

  const settings = useSelector(selectSettings)

  const [isFocused, setIsFocused] = useState(false)

  const [isVisible, setIsVisible] = useState(false)

  const [candidators, setCandidators] = useState<CommonCode[]>([])

  const [selectedId, setSelectedId] = useState<any>('')

  const [isDisabled, setIsDisabled] = useState<any>(false)

  // 新版目录且是剂量单位/从医嘱处方列表点击的
  const newMLFlag = yzcfkf && (field == 'doseUnit') && settings?.[Checks.MLKG] == 1

  const inputRef = useRef<Input>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus()
    },
    select: () => {
      inputRef.current?.select()
    },
    isDropdownOpen: () => isVisible,
  }))

  let codeCategory: CommonCodeCategory | undefined = undefined

  let placeholder = ''
  let width = kskdTag || ydkdFlag ? '2.4rem' : '3rem'
  let pattern: RegExp | undefined = undefined
  switch (field) {
    case 'groupNumber':
      placeholder = '组号'
      pattern = NumberPattern
      break
    case 'normalUsemethod':
      codeCategory =
        recipeCategory === RecipeCategory.Tcm ||
          recipeCategory === RecipeCategory.TcmGranules
          ? CommonCodeCategory.TcmUsage
          : CommonCodeCategory.Usage
      placeholder = '用法'
      width = kskdTag || ydkdFlag ? '4rem' : '4rem'
      break
    case 'durgCategory':
      codeCategory = CommonCodeCategory.Durg
      placeholder = '药品分类'
      break
    case 'accountUnit':
      codeCategory = CommonCodeCategory.Unit
      placeholder = '账簿单位'
      break
    case 'doesUnit':
      codeCategory = CommonCodeCategory.Unit
      break
    case 'doseCategory':
      codeCategory = CommonCodeCategory.Dosago
      placeholder = '剂型'
      break
    case 'normalFrequency':
      codeCategory = CommonCodeCategory.Frequency
      placeholder = '频次'
      width = kskdTag || ydkdFlag ? '4rem' : '4.5rem'
      break
    case 'normalDose':
      placeholder = '剂量'
      pattern = NormalDosePattern
      break
    case 'useDays':
      placeholder = '天数'
      pattern = NumberPattern
      break
    case 'retailPrice':
      placeholder = '单价'
      width = kskdTag || ydkdFlag ? '3.2rem' : '4.5rem'
      pattern = DecimalPattern
      break
    case 'doseUnit':
      codeCategory = CommonCodeCategory.Unit
      placeholder = '单位'
      break
    case 'drugCount':
      placeholder = '数量'
      pattern = NumberPatternTwoCount
      break
    case 'countPerTip':
      placeholder = '每帖数量'
      pattern = DecimalPattern
      width = kskdTag || ydkdFlag ? '4rem' : '5rem'
      break
    case 'discount':
      placeholder = '折扣%'
      width = kskdTag || ydkdFlag ? '3.2rem' : '4.5rem'
      pattern = DecimalPattern
      break
    default:
      break
  }

  const overlay = (
    <Col style={{ overflow: 'hidden' }}>
      <Row
        style={{
          width: '36rem',
          maxHeight: '12rem',
          overflowY: 'auto',
        }}
      >
        {candidators.map((c) => (
          <ListItem
            key={c.id}
            code={c}
            active={selectedId === c.id}
            onSelect={(c) => {
              onChange && onChange(c.name, c.codeCalc)
              setIsVisible(false)
              setSelectedId(c.id)
            }}
          />
        ))}
      </Row>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '2rem',
          lineHeight: '2rem',
          color: theme.c1,
          textAlign: 'center',
          fontWeight: 'bold',
          backgroundColor: theme.c3,
          cursor: 'pointer',
        }}
        onClick={() => setIsVisible(false)}
      >
        关闭(ESC)
        {field === 'normalUsemethod' && isTbEdit && record?.drugType == 0 ? (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 10,
              cursor: 'pointer',
            }}
            onClick={() => {
              onChangeAll && onChangeAll()
              setIsVisible(false)
            }}
          >
            用法同步
          </span>
        ) : (
          <></>
        )}
      </div>
    </Col>
  )

  const XBMLOverlay = (
    <Menu className={styles.doseUnitSelect}>
      {candidators.map((c) => (
        <Menu.Item
          style={{ backgroundColor: (selectedId ? selectedId : value) == c.id  ? theme.c3 : undefined }}
          key={c.id}
          onClick={() => {
            onChange && onChange(c.id)
            setIsVisible(false)
            setSelectedId(c.id)
            setIsDisabled(c.id == '适量' ? true : false)
          }}
        >
          {c.name}
        </Menu.Item>
      ))}
    </Menu>
  )



  const flag = yzcfkf && field == 'normalDose' && settings?.[Checks.MLKG] == 1

  const input = (
    <Input
      autoFocus={autoFocus}
      ref={inputRef}
      {...others}
      style={{
        ...style,
        width: noSize ? style?.width : width,
        height: noSize ? style?.height : '2rem',
        padding: noSize ? undefined : 0,
        textAlign: noSize ? undefined : 'center',
      }}
      disabled={disabled || (flag ? isDisabled : disabled)}
      maxLength={field === 'normalUsemethod' ? 5 : field === 'accountUnit' ? 3 : undefined}
      value={value}
      onClick={() => {
        setIsVisible(true)
      }}
      placeholder={placeholderInput}
      onFocus={() => {
        setIsFocused(true)
        setIsVisible(true)
      }}
      onBlur={async () => {
        await sleep(250)
        setIsFocused(false)
        setIsVisible(false)
        onBlur && onBlur(value)
        if (clearDataFlag) {
          setCandidators([])
        }
      }}
      onChange={(e) => {
        const text = e.target.value
        if (pattern && text && !pattern.test(text)) {
          return
        }
        let candidator = candidators.find(
          (c) =>
            c.code.toLocaleLowerCase() === text.toLocaleLowerCase() ||
            c.name === text
        )
        if (!candidator) {
          candidator = candidators.find((c) =>
            c.code.toLocaleLowerCase().includes(text.toLowerCase())
          )
        }
        if (candidator) {
          setSelectedId(candidator.id)
        }

        onChange && onChange(e.target.value)
      }}
      onKeyDown={(e) => {
        onKeyDown && onKeyDown(e)
        e.stopPropagation()
        if (!selectedId) {
          return
        }
        let index = candidators.findIndex((c) => c.id === selectedId)
        if (index < 0) {
          return
        }
        switch (e.key) {
          case 'Escape':
            setIsVisible(false)
            break
          case 'ArrowRight':
            index = index + 1
            break
          case 'ArrowLeft':
            index = index - 1
            break
          case 'ArrowDown':
            index = newMLFlag ? index + 1 : index + ColumnsPerRow
            break
          case 'ArrowUp':
            index = newMLFlag ? index - 1 : index - ColumnsPerRow
            break
        }
        if (index < 0 || index >= candidators.length) {
          return
        }
        setSelectedId(candidators[index].id )
      }}
      onPressEnter={(e) => {
        e.stopPropagation()
        const code = candidators.find((c) => c.id === selectedId)
        if (code && onChange) {
          onChange(code.name)
        }
        setIsVisible(false)
        // if (code && onPressEnter) {
        onPressEnter && onPressEnter(code?.name)
        // }
      }}
    />
  )

  const getDoseUnitData = (v: any) => {
    let list: any = []
    // 数量单位只能选择最小包装单位 包装单位
    const accountUnit = editTableDoseUnit ? v?.accountUnitML : v?.accountUnit
    if (v?.materialsCategory == 0) {
      // 中药药品 : 最小包装单位 包装单位
      if (v?.drugCategory === '中药材/饮片') {
        if (v?.splitTag == 1) {
          list = [
            {
              name: '适量',
            },
            v?.minPackUnit && {
              name: v?.minPackUnit,
            },
          ].filter(Boolean);
        } else {
          list = [
            {
              name: '适量',
            },
            v?.minPackUnit && {
              name: v?.minPackUnit,
            },
            accountUnit && {
              name: accountUnit,
            }
          ].filter(Boolean);
        }
      } else {
        // 非中药药品 : 容量单位 成分含量单位 最小包装单位 包装单位
        if (v?.splitTag == 1) {
          list = [
            {
              name: '适量',
            },
            v?.capacityUnit && {
              name: v?.capacityUnit,
            },
            v?.componentsUnit && {
              name: v?.componentsUnit,
            },
            v?.minPackUnit && {
              name: v?.minPackUnit,
            },
          ].filter(Boolean);
        } else {
          list = [
            {
              name: '适量',
            },
            v?.capacityUnit && {
              name: v?.capacityUnit,
            },
            v?.componentsUnit && {
              name: v?.componentsUnit,
            },
            v?.minPackUnit && {
              name: v?.minPackUnit,
            },
            accountUnit && {
              name: accountUnit,
            }
          ].filter(Boolean);
        }
      }
    } else {
      // 材料/器械/其他 : 最小包装单位 包装单位
      list = [
        {
          name: '适量',
        },
        v?.minPackUnit && {
          name: v?.minPackUnit,
        },
        accountUnit && {
          name: accountUnit,
        }
      ].filter(Boolean);
    }

    // 关键：过滤掉 false、null、undefined、空字符串
    const uniqueMap = new Map();
    list.forEach((item: any) => {
      uniqueMap.set(item.name, item);
    });
    list = Array.from(uniqueMap.values());

    setCandidators(
      list?.map((item: any) => ({
        name: item.name,
        id: item.name,
      }))
    )
  }

  useEffect(() => {
    if (newMLFlag && editTableDoseUnit) {
      getDoseUnitData(record)
    } else {
      if (codeCategory && isFocused && candidators.length === 0) {
        dispatch(
          getCodeCompletions({
            category: codeCategory,
            keyword: '',
          })
        )
          .then(unwrapResult)
          .then((cs) => {
            setCandidators(cs)
            if (cs.length > 0) {
              const selectName = selectData?.[field]
              const id = cs?.find((v) => v?.name == selectName)?.id
              if (id) {
                setSelectedId(id)
              } else {
                setSelectedId(cs[0].id)
              }
              if (isFocused) {
                setIsVisible(true)
              }
            }
          })
      }
    }
  }, [dispatch, candidators, isFocused, selectData])

  useEffect(() => {
    if (newMLFlag && !editTableDoseUnit) {
      getDoseUnitData(selectData)
    }
    setIsDisabled(currentDoseUnit == '适量')
    setSelectedId(currentDoseUnit ? currentDoseUnit : selectData?.doseUnit ? selectData?.doseUnit : candidators?.[0]?.id || '适量')
  }, [selectData ,currentDoseUnit])

  return (
    <div style={{ position: 'relative' }}>
      {codeCategory ? (
        <Dropdown
          overlay={newMLFlag ? XBMLOverlay : overlay}
          disabled={disabled}
          trigger={[]}
          visible={isVisible && candidators.length > 0}
          onVisibleChange={(v) => setIsVisible(v)}
        >
          {input}
        </Dropdown>
      ) : (
        input
      )}
      {!noSize && (
        <div
          style={
            value || value === 0
              ? {
                color: theme.tc1,
                padding: '0px 6px',
                backgroundColor: theme.pn,
                fontSize: '0.5rem',
                position: 'absolute',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                left: 4,
                top: -8,
                pointerEvents: 'none',
                transition: 'all .3s ease',
              }
              : {
                color: theme.tc3,
                fontSize: '0.875rem',
                position: 'absolute',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                left: '50%',
                top: '50%',
                pointerEvents: 'none',
                transform: 'translate(-50%, -50%)',
                transition: 'all .3s ease',
              }
          }
        >
          {placeholder}
        </div>
      )}
    </div>
  )
}

export const RecipeItemFieldInput = forwardRef(RealRecipeItemFieldInput)
